import { getBorgeeTransport } from '../client-core/transport';
import { ApiError } from './api';

export type SetupReason =
  | 'node_missing'
  | 'npm_missing'
  | 'node_version_unsupported'
  | 'node_probe_failed'
  | 'platform_unsupported'
  | 'installation_failed'
  | 'installation_timeout'
  | 'verification_failed'
  | 'apply_failed'
  | 'apply_timeout';

type RuntimeReason =
  | 'api_key_invalid'
  | 'quota_exceeded'
  | 'network_unreachable'
  | 'runtime_crashed'
  | 'runtime_timeout'
  | 'unknown';

type SetupWorkIdentity = {
  work_id: string;
  attempt: number;
  work_age_ms: number;
  phase_age_ms: number;
};

export type ManagedAgentSetupWork = SetupWorkIdentity & (
  | { state: 'checking' | 'installing' | 'verifying' | 'waiting_to_apply' | 'applying' }
  | { state: 'failed'; reason_code: SetupReason; retry_in_ms?: number }
  | { state: 'applied'; accepted_at?: string }
);

export type ManagedAgentProvisioningStatusDTO = {
  agent_id: string;
  desired: 'enabled' | 'disabled' | 'unbound' | 'machine_missing';
  runtime: { connected: boolean } & (
    | { state: 'online' | 'offline' }
    | { state: 'error'; reason: RuntimeReason }
  );
  helper: 'connected' | 'offline';
  observation: 'fresh' | 'unreported' | 'unsupported' | 'stale' | 'awaiting_assignment';
  observed_at?: string;
  sync_reason_code?: 'spec_fetch_failed' | 'spec_invalid';
  setup?: { state: 'pending_assignment'; retry_in_ms?: number } | ManagedAgentSetupWork;
};

const setupReasons: readonly SetupReason[] = [
  'node_missing', 'npm_missing', 'node_version_unsupported', 'node_probe_failed',
  'platform_unsupported', 'installation_failed', 'installation_timeout',
  'verification_failed', 'apply_failed', 'apply_timeout',
];
const runtimeReasons: readonly RuntimeReason[] = [
  'api_key_invalid', 'quota_exceeded', 'network_unreachable',
  'runtime_crashed', 'runtime_timeout', 'unknown',
];
const workStates = ['checking', 'installing', 'verifying', 'waiting_to_apply', 'applying', 'applied', 'failed'];
const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const timestampPattern = /^(\d{4})-(\d{2})-(\d{2})T([01]\d|2[0-3]):([0-5]\d):([0-5]\d)(?:\.\d{1,9})?(?:Z|[+-](?:[01]\d|2[0-3]):[0-5]\d)$/;

const invalidResponse = (): never => {
  throw new TypeError('Invalid managed Agent provisioning status response');
};

const record = (value: unknown): Record<string, unknown> => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return invalidResponse();
  return value as Record<string, unknown>;
};

const checkKeys = (value: Record<string, unknown>, required: string[], optional: string[] = []): void => {
  if (required.some((key) => !Object.hasOwn(value, key))
    || Object.keys(value).some((key) => !required.includes(key) && !optional.includes(key))) invalidResponse();
};

const isEnum = (value: unknown, choices: readonly string[]): boolean =>
  typeof value === 'string' && choices.includes(value);

const isCount = (value: unknown, maximum: number, minimum = 0): boolean =>
  typeof value === 'number' && Number.isSafeInteger(value) && value >= minimum && value <= maximum;

const isTimestamp = (value: unknown): boolean => {
  if (typeof value !== 'string') return false;
  const match = timestampPattern.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const leapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const monthDays = [31, leapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return month >= 1 && month <= 12 && day >= 1 && day <= monthDays[month - 1]
    && Number.isFinite(Date.parse(value));
};

const checkRuntime = (value: unknown): void => {
  const runtime = record(value);
  checkKeys(runtime, runtime.state === 'error' ? ['connected', 'state', 'reason'] : ['connected', 'state']);
  if (typeof runtime.connected !== 'boolean' || !isEnum(runtime.state, ['online', 'offline', 'error'])
    || (runtime.state === 'error' && !isEnum(runtime.reason, runtimeReasons))) invalidResponse();
};

const checkSetup = (value: unknown): void => {
  const setup = record(value);
  if (setup.state === 'pending_assignment') {
    checkKeys(setup, ['state'], ['retry_in_ms']);
  } else {
    const required = ['state', 'work_id', 'attempt', 'work_age_ms', 'phase_age_ms'];
    if (setup.state === 'failed') required.push('reason_code');
    const optional = setup.state === 'failed' ? ['retry_in_ms'] : setup.state === 'applied' ? ['accepted_at'] : [];
    checkKeys(setup, required, optional);
    if (!isEnum(setup.state, workStates)
      || typeof setup.work_id !== 'string' || !uuidPattern.test(setup.work_id)
      || !isCount(setup.attempt, 2_147_483_647, 1)
      || !isCount(setup.work_age_ms, 86_400_000) || !isCount(setup.phase_age_ms, 86_400_000)
      || (setup.phase_age_ms as number) > (setup.work_age_ms as number)
      || (setup.state === 'failed' && !isEnum(setup.reason_code, setupReasons))
      || (Object.hasOwn(setup, 'accepted_at') && !isTimestamp(setup.accepted_at))) invalidResponse();
  }
  if (Object.hasOwn(setup, 'retry_in_ms') && !isCount(setup.retry_in_ms, 3_600_000)) invalidResponse();
};

const decodeStatus = (value: unknown, agentId: string): ManagedAgentProvisioningStatusDTO => {
  const status = record(value);
  checkKeys(status, ['agent_id', 'desired', 'runtime', 'helper', 'observation'], ['observed_at', 'sync_reason_code', 'setup']);
  if (typeof status.agent_id !== 'string' || status.agent_id.length === 0 || status.agent_id !== agentId
    || !isEnum(status.desired, ['enabled', 'disabled', 'unbound', 'machine_missing'])
    || !isEnum(status.helper, ['connected', 'offline'])
    || !isEnum(status.observation, ['fresh', 'unreported', 'unsupported', 'stale', 'awaiting_assignment'])) invalidResponse();
  checkRuntime(status.runtime);

  const hasObservation = status.observation === 'fresh' || status.observation === 'stale';
  if (status.desired !== 'enabled' && (status.helper !== 'offline' || status.observation !== 'unreported')) invalidResponse();
  if (status.helper === 'offline' && status.observation !== 'unreported') invalidResponse();
  if (hasObservation) {
    if (!isTimestamp(status.observed_at)) invalidResponse();
    if (Object.hasOwn(status, 'sync_reason_code') && !isEnum(status.sync_reason_code, ['spec_fetch_failed', 'spec_invalid'])) invalidResponse();
    if (Object.hasOwn(status, 'setup')) checkSetup(status.setup);
    if (Object.hasOwn(status, 'setup') === Object.hasOwn(status, 'sync_reason_code')) invalidResponse();
  } else if (['observed_at', 'setup', 'sync_reason_code'].some((key) => Object.hasOwn(status, key))) {
    invalidResponse();
  }
  return status as ManagedAgentProvisioningStatusDTO;
};

export const fetchManagedAgentProvisioningStatus = async (
  agentId: string,
  signal: AbortSignal,
): Promise<ManagedAgentProvisioningStatusDTO> => {
  signal.throwIfAborted();
  const response = await getBorgeeTransport().request({
    path: `/api/v1/agents/${encodeURIComponent(agentId)}/provisioning-status`,
    method: 'GET',
    responseType: 'json',
    signal,
  });
  signal.throwIfAborted();
  if (response.status < 200 || response.status >= 300) {
    const data = response.kind === 'json' && typeof response.data === 'object' && response.data !== null
      ? response.data as Record<string, unknown>
      : undefined;
    throw new ApiError(
      response.status,
      typeof data?.error === 'string' ? data.error : response.statusText || 'Unable to read Agent setup status',
      typeof data?.code === 'string' ? data.code : undefined,
    );
  }
  if (response.kind !== 'json') return invalidResponse();
  return decodeStatus(response.data, agentId);
};
